import { contactName } from "@/lib/contacts";
import { sameValues } from "@/lib/form";
import { formatDateTime } from "@/lib/utils";
import type { ContactInput } from "@/lib/queries";
import {
  type Draft, detailEditable, detailInput, draftOf, editable, parseDetail, parseDraft, parseOwn,
} from "@/lib/record-fields";
import type {
  AccidentRecord, ContractAgreement, ContractAgreements, ContractCheck, ContractField, ContractNeed, ContractSent,
  RecordField, RecordSection, Role,
} from "@/lib/types";

/**
 * What core-api says when a contract did not go out (ADR-134 in platform): a flat `{code, detail}`
 * body, and `fields` naming the lead's values to fix, by their labels on the form.
 */
export type ContractRefusalCode =
  | "not_set_up"
  | "no_firm"
  | "firm_inactive"
  | "missing_fields"
  | "invalid_value"
  | "no_agreement"
  | "duplicate"
  | "service_failed"
  | "service_refused"
  | "wrong_firm";

export interface ContractRefusal {
  code: ContractRefusalCode;
  /** The sentence to show. */
  detail: string;
  fields: ContractField[];
}

function isField(value: unknown): value is ContractField {
  const field = value as { key?: unknown; label?: unknown } | null;
  return !!field && typeof field.key === "string" && typeof field.label === "string";
}

/** The refusal in an `ApiError`, or null when the error carries none (no answer, an ended session). */
export function contractRefusal(error: unknown): ContractRefusal | null {
  if (!(error instanceof Error)) return null;
  const failure = error as Error & { body?: unknown };
  const body = failure.body as { code?: unknown; fields?: unknown } | null | undefined;
  if (!body || typeof body.code !== "string") return null;
  return {
    code: body.code as ContractRefusalCode,
    detail: failure.message,
    fields: Array.isArray(body.fields) ? body.fields.filter(isField) : [],
  };
}

/**
 * What the dialog says, if anything: the values to fill in on the record, or one sentence. A
 * refusal after a press comes first, since it is the newest word; before any press, the check's own
 * reason (ACL-419 names the missing values before the click). `pressed` tells the two lists apart:
 * after a press the rep is asked to send again.
 */
export type ContractNotice =
  | { kind: "fill"; fields: ContractField[]; pressed: boolean }
  | { kind: "reason"; detail: string; tone: "warning" | "danger" };

export function contractNotice(
  check: ContractCheck | undefined,
  refusal: ContractRefusal | null,
): ContractNotice | null {
  if (refusal) {
    if (refusal.code === "missing_fields" && refusal.fields.length > 0) {
      return { kind: "fill", fields: refusal.fields, pressed: true };
    }
    return { kind: "reason", detail: refusal.detail, tone: "danger" };
  }
  if (!check || check.can_send) return null;
  if (check.reason === "missing_fields" && check.fields.length > 0) {
    return { kind: "fill", fields: check.fields, pressed: false };
  }
  return check.detail ? { kind: "reason", detail: check.detail, tone: "warning" } : null;
}

/**
 * A value Send contract was refused for, marked on the record (ACL-541): what is said under the field,
 * and the text it held when it was refused (null: it was empty). The mark stays while the field still
 * holds that text, so it goes the moment the value is filled in or changed.
 */
export interface ContractProblem {
  message: string;
  was: string | null;
}

/** The record's values to fix before Send contract, by the form's key. */
export type ContractAsk = ReadonlyMap<string, ContractProblem>;

/**
 * The values a check or a refusal names to fix on the record (ACL-541), or null when it names none:
 * an empty one is required, and a value in the wrong shape is said as core-api says it. `textOf`
 * reads a field's text on the record now, which a wrong value is marked against.
 */
export function contractAsk(
  found: { code: string | null; fields: ContractField[]; detail: string | null },
  textOf: (key: string) => string | null,
): ContractAsk | null {
  if (found.fields.length === 0) return null;
  if (found.code === "missing_fields") {
    return new Map(found.fields.map((f) => [f.key, { message: `${f.label} is required to send the contract.`, was: null }]));
  }
  if (found.code === "invalid_value") {
    return new Map(
      found.fields.map((f) => [f.key, { message: found.detail ?? `${f.label} is not valid.`, was: textOf(f.key) }]),
    );
  }
  return null;
}

/** What closing the Send contract dialog marks on the record (ACL-541): the values its refusal names,
 *  else the ones the check says are missing, as the dialog listed them; null when it named none. */
export function contractToFix(
  check: ContractCheck | undefined,
  refusal: ContractRefusal | null,
): { code: string | null; fields: ContractField[]; detail: string | null } | null {
  if (refusal) return refusal.fields.length > 0 ? refusal : null;
  if (check && !check.can_send && check.reason === "missing_fields" && check.fields.length > 0) {
    return { code: check.reason, fields: check.fields, detail: check.detail };
  }
  return null;
}

/** What is said under a field the rep was asked to fix, while it still holds what was refused; null
 *  once it is filled in or changed, or when it was never asked for. */
export function contractProblem(problem: ContractProblem | undefined, text: string | null): string | null {
  return problem && text === problem.was ? problem.message : null;
}

/** The toast when Send contract stops at the record: the fields it marked, by their labels. */
export function askedMessage(fields: ContractField[], code: string | null, detail: string | null): {
  title: string;
  detail: string;
} {
  if (code === "invalid_value" && detail) return { title: "Fix the highlighted field", detail };
  return {
    title: "Fill in the highlighted fields",
    detail: `Needed to send the contract: ${fields.map((f) => f.label).join(", ")}.`,
  };
}

/** Whose agreement the dialog says goes out: the lead's firm by its label, once the check names it. */
export function agreementOf(check: ContractCheck | undefined): string {
  return check?.firm ? `${check.firm}'s agreement` : "Their firm's agreement";
}

/**
 * What Send contract offers to choose (ACL-578), or null when the dialog sends as it did before: no
 * list (a core-api or contract service before ACL-578, or no answer), or an empty one.
 */
export function offerOf(found: ContractAgreements | undefined): ContractAgreements | null {
  return found && found.agreements.length > 0 ? found : null;
}

/** The agreement the dialog will send: the rep's pick, else the suggested one, else none. */
export function chosenOf(offer: ContractAgreements, picked: number | null): ContractAgreement | null {
  const id = picked ?? offer.suggested;
  return offer.agreements.find((a) => a.id === id) ?? null;
}

/**
 * The check the dialog reads once there is a choice (ACL-578): what the chosen agreement still needs,
 * under its firm, so the record's notice, its dimmed button and what closing it marks work as they
 * did. With none chosen yet, nothing can be sent and there is nothing to fill in.
 */
export function chosenCheck(chosen: ContractAgreement | null): ContractCheck {
  if (!chosen) return { can_send: false, reason: null, detail: null, fields: [], firm: null };
  const fill = chosen.fields.length > 0;
  return {
    can_send: !fill,
    reason: fill ? "missing_fields" : null,
    detail: fill ? `Fill these in first: ${chosen.fields.map((f) => f.label).join(", ")}.` : null,
    fields: chosen.fields,
    firm: chosen.firm,
  };
}

/** The agreements by firm, in core-api's order: the contact's own firm first, then the others by name. */
export function agreementGroups(
  agreements: readonly ContractAgreement[],
): { firmId: number; firm: string; agreements: ContractAgreement[] }[] {
  const groups: { firmId: number; firm: string; agreements: ContractAgreement[] }[] = [];
  for (const agreement of agreements) {
    const group = groups.find((g) => g.firmId === agreement.firm_id);
    if (group) group.agreements.push(agreement);
    else groups.push({ firmId: agreement.firm_id, firm: agreement.firm, agreements: [agreement] });
  }
  return groups;
}

/** An agreement's version, as the list says it under its name. */
export function versionOf(agreement: ContractAgreement): string {
  const language = agreement.language === "es" ? "Spanish" : "English";
  return agreement.is_minor ? `${language}, minor (a parent or guardian signs)` : language;
}

/**
 * What the dialog says under the choice, as the contract service's own manual send does: why nothing is
 * preselected, that the rep moved off the suggested agreement, and that another firm's agreement makes
 * that firm the contact's once it is sent. `firmId` is the contact's firm now, or null for none.
 */
export function choiceLines(
  offer: ContractAgreements,
  chosen: ContractAgreement | null,
  firmId: number | null,
): string[] {
  const lines: string[] = [];
  const suggested = offer.agreements.find((a) => a.id === offer.suggested) ?? null;
  if (!suggested && offer.note) lines.push(offer.note);
  if (chosen && suggested && chosen.id !== suggested.id) {
    lines.push(`Changed from the suggested agreement (${suggested.name}).`);
  }
  if (chosen && chosen.firm_id !== firmId) {
    lines.push(
      firmId === null
        ? `Sending it sets the contact's firm to ${chosen.firm}.`
        : `Sending it changes the contact's firm to ${chosen.firm}.`,
    );
  }
  return lines;
}

/**
 * A sent agreement whose link did not go out by text (ACL-423): the agreement exists, so the dialog
 * keeps it on screen with why and the link, and offers no second press, which would make another
 * agreement. Null for anything the toast can say.
 */
export function untexted(
  sent: ContractSent,
): { problem: string; link: string; emailedTo: string | null } | null {
  if (sent.status !== "sent" || sent.texted || !sent.signing_url) return null;
  return {
    problem: sent.text_problem || "The text did not go out.",
    link: sent.signing_url,
    // DocuSeal's own e-mail may still have reached the lead (ACL-423).
    emailedTo: emailedTo(sent),
  };
}

/** Who DocuSeal e-mailed the agreement to, or null when it did not (ACL-423). */
function emailedTo(sent: ContractSent): string | null {
  return sent.emailed && sent.email_to ? sent.email_to : null;
}

/** The toast after a press that worked: sent, or only checked while live sending is off. */
export function sentMessage(sent: ContractSent): { title: string; detail: string } {
  if (sent.status === "sent") {
    const to = emailedTo(sent);
    // Outside the lead's hours the text waits for their 8 am (ACL-630).
    const texted = sent.text_scheduled_for
      ? `The signing link will be texted ${formatDateTime(sent.text_scheduled_for)}, the lead's 8 am`
      : "The signing link went out by text";
    const how = to ? `${texted}, and DocuSeal e-mailed it to ${to}.` : `${texted}.`;
    return { title: "Contract sent", detail: `${sent.agreement}. ${how}` };
  }
  return {
    title: "Contract checked",
    detail: `${sent.agreement} would go out. Nothing was sent: live sending is not switched on yet.`,
  };
}

// --- every value the agreement is filled with, checked and fixed in the dialog (ACL-604) -----------------

/**
 * One value the chosen agreement is filled with, as the dialog draws it (ACL-604): what core-api says it
 * needs, the field on this person's form that holds it (null when their form does not show it), and how
 * the dialog changes it: one of the contact's own details (`detail`, `PATCH /leads/{id}`, as the record's
 * details panel does, by the same per-role rules), one of the form's answers (`answer`,
 * `PATCH /leads/{id}/fields`), or not here (`read`): a field their role may only read, or one their form
 * does not show.
 */
export interface AgreementValue {
  need: ContractNeed;
  field: RecordField | null;
  how: "detail" | "answer" | "read";
}

/** The chosen agreement's values, in core-api's order, each with its field on the form `sections` (the
 *  whole form, as `GET /leads/{id}/fields` sends it to this person). */
export function agreementValues(
  needs: readonly ContractNeed[],
  sections: readonly RecordSection[],
  role: Role | undefined,
): AgreementValue[] {
  const byKey = new Map(sections.flatMap((s) => s.fields.map((f) => [f.key, f] as const)));
  return needs.map((need) => {
    const field = byKey.get(need.key) ?? null;
    if (field === null) return { need, field, how: "read" };
    if (field.system) return { need, field, how: detailEditable(field, role) ? "detail" : "read" };
    return { need, field, how: editable(field) ? "answer" : "read" };
  });
}

/** Said under a required value this person cannot fill in here, while it is empty (ACL-604). */
export const READ_ONLY_PROBLEM = "Ask a supervisor to fill this in: the agreement cannot go out without it.";

/** Today on this computer's clock, `YYYY-MM-DD`: a date after it is in the future. */
export function todayKey(now: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/** A value's draft: what the rep typed, else what the record holds. */
export function draftFor(value: AgreementValue, drafts: Readonly<Record<string, Draft>>): Draft {
  return drafts[value.need.key] ?? (value.field ? draftOf(value.field) : "");
}

/** A changed value's draft checked as its save checks it: a detail as the details panel, an answer as
 *  the form (which refuses emptying a field the form requires). One left as the record has it is only
 *  read, never sent, so the form's own required rule is not its. */
function parsed(value: AgreementValue & { field: RecordField }, draft: Draft, changed: boolean) {
  // A phone already on the record stands, whatever its spelling: only a new one has to be a US number.
  if (value.how === "detail" && !changed && value.field.key === "phone" && draft !== "") return parseDraft(value.field, draft);
  if (value.how === "detail") return parseDetail(value.field, draft);
  return changed ? parseOwn(value.field, draft) : parseDraft(value.field, draft);
}

/**
 * What is wrong with one value before Send (ACL-604), or null: a draft its save would refuse, a required
 * one left empty, a date in the future. A value the rep cannot change here is wrong only when it is
 * required and empty, which `missing` (what core-api says the agreement still needs) tells for a field
 * their form does not show. `today` is `todayKey()`.
 */
export function valueProblem(
  value: AgreementValue,
  drafts: Readonly<Record<string, Draft>>,
  missing: ReadonlySet<string>,
  today: string,
): string | null {
  const { need, field, how } = value;
  if (how === "read" || field === null) {
    return need.required && missing.has(need.key) ? READ_ONLY_PROBLEM : null;
  }
  const draft = draftFor(value, drafts);
  const checked = parsed({ ...value, field }, draft, !sameValues(draft, draftOf(field)));
  if (!checked.ok) return checked.problem;
  if (checked.value === null) return need.required ? `${need.label} is required to send the contract.` : null;
  if (field.type === "date" && String(checked.value) > today) return `${need.label} cannot be in the future.`;
  return null;
}

/**
 * What Send saves before it presses (ACL-604): the details and the answers the rep changed here, each
 * as its save sends it. A value whose draft is still the record's is not sent, so a press with nothing
 * changed saves nothing. Only called once `valueProblem` says nothing for every value.
 */
export function agreementChanges(
  values: readonly AgreementValue[],
  drafts: Readonly<Record<string, Draft>>,
): { details: ContactInput; answers: Record<string, unknown> } {
  const details: ContactInput = {};
  const answers: Record<string, unknown> = {};
  for (const value of values) {
    const { field, how } = value;
    const draft = drafts[value.need.key];
    if (field === null || how === "read" || draft === undefined || sameValues(draft, draftOf(field))) continue;
    const checked = parsed({ ...value, field }, draft, true);
    if (!checked.ok) continue;
    if (how === "detail") Object.assign(details, detailInput(field.key, checked.value));
    else answers[field.key] = checked.value;
  }
  return { details, answers };
}

/** A pick, saved as it is made; a typed value is saved when the rep leaves its field (ACL-653). */
export function savesOnPick(field: Pick<RecordField, "type">): boolean {
  return !["text", "long_text", "number", "email", "phone", "datetime"].includes(field.type);
}

/**
 * The value to save now that the rep has finished with it (ACL-653): one of the form's answers, valid
 * and changed, by key, as `PATCH /leads/{id}/fields` takes it. Null for anything else: an invalid value
 * stays marked in the window, and the contact's own details wait for Send.
 */
export function answerToSave(
  value: AgreementValue,
  drafts: Readonly<Record<string, Draft>>,
  missing: ReadonlySet<string>,
  today: string,
): Record<string, unknown> | null {
  if (value.how !== "answer" || valueProblem(value, drafts, missing, today) !== null) return null;
  const { answers } = agreementChanges([value], drafts);
  return Object.keys(answers).length > 0 ? answers : null;
}

/** Whether the rep has changed a value in the window (ACL-653): `before` holds what each value they
 *  touched read before they did. The call form's answers it opened with are still on the form. */
export function changedHere(
  before: Readonly<Record<string, Draft>>,
  drafts: Readonly<Record<string, Draft>>,
): boolean {
  return Object.entries(before).some(([key, was]) => !sameValues(drafts[key], was));
}

/** What a refusal after the press says under one of the dialog's values, or null when it names none. */
export function refusedValue(refusal: ContractRefusal | null, key: string): string | null {
  if (!refusal || !refusal.fields.some((f) => f.key === key)) return null;
  return refusal.code === "missing_fields" ? "Required to send the contract." : refusal.detail;
}

// --- who gets an agreement: everyone in the accident (ACL-671) --------------------------------------------

/** One person in the accident as the dialog's checklist lists them: whether an agreement can go out to
 *  them now, and why not. */
export interface AgreementParty {
  id: string;
  name: string;
  /** signed: nothing to send. ready: the rep may tick them. fix: their record lacks a value, or their firm
   *  cannot take them; said with the reason and Open. checking: the check has not answered. */
  state: "signed" | "ready" | "fix" | "checking";
  reason: string | null;
  /** How the link reaches them: on the same number as the caller, in their own name, or their own. */
  texted: string;
}

/** The others in the accident with the caller's number, or their own number as given; a record with no
 *  number (one the reader may not open is never listed) is said to have none. Pure. */
export function agreementParty(
  party: Pick<AccidentRecord, "id" | "first_name" | "last_name" | "phone" | "agreement" | "may_open">,
  check: ContractCheck | undefined,
  caller: { phone: string; first_name: string | null },
  format: (phone: string) => string,
): AgreementParty {
  const name = contactName(party) ?? "No name";
  const first = name.split(" ")[0] ?? name;
  const texted = !party.phone
    ? "no number to text"
    : party.phone === caller.phone
      ? `texted to ${caller.first_name ?? "the caller"}'s number, in ${first}'s name`
      : `texted to ${format(party.phone)}`;
  if (party.agreement?.state === "signed") return { id: party.id, name, state: "signed", reason: "already signed", texted };
  if (check === undefined) return { id: party.id, name, state: "checking", reason: null, texted };
  if (check.can_send) return { id: party.id, name, state: "ready", reason: null, texted };
  const fields = check.fields.map((f) => f.label);
  const reason =
    check.reason === "missing_fields" && fields.length > 0
      ? `fill in ${fields.join(", ")} on their record first`
      : (check.detail ?? "cannot be sent yet");
  return { id: party.id, name, state: "fix", reason, texted };
}

/** What the toast says of the others' agreements, sent one after another: how many went, counting the
 *  contact's own when it went too (`own`), and each one that did not, by name and reason. Pure. */
export function partiesSentMessage(
  results: readonly { name: string; sent: ContractSent | null; problem: string | null }[],
  own = true,
): { title: string; detail: string; tone: "success" | "warning" } | null {
  if (results.length === 0) return null;
  const went = results.filter((r) => r.sent !== null && r.problem === null);
  const failed = results.filter((r) => r.problem !== null);
  const untextedOnes = results.filter((r) => r.sent !== null && r.problem === null && r.sent.status === "sent" && !r.sent.texted);
  const names = (list: typeof results) => list.map((r) => r.name).join(", ");
  const parts: string[] = [];
  if (went.length > 0) parts.push(`${own ? "Also sent" : "Sent"} to ${names(went)}.`);
  for (const r of untextedOnes) parts.push(`${r.name}'s link did not go out by text: ${r.sent?.text_problem ?? "see their record"}.`);
  for (const r of failed) parts.push(`${r.name}: ${r.problem}`);
  const total = went.length + (own ? 1 : 0);
  const sentWord = total === 1 ? "Contract sent" : `${total} agreements sent`;
  const clean = failed.length === 0 && untextedOnes.length === 0;
  const title = clean
    ? sentWord
    : failed.length === results.length
      ? own ? "The others were not sent" : "Nothing was sent"
      : `${sentWord}, ${failed.length + untextedOnes.length} to look at`;
  return { title, detail: parts.join(" "), tone: clean ? "success" : "warning" };
}
